import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import can from 'ember-can/helpers/can';
import { t } from 'ember-intl';

import Button from 'umg-ember-backend/components/ui/button';
import ContentWithSidebar from 'umg-ember-backend/components/ui/content-with-sidebar';
import Form from 'umg-ember-backend/components/ui/form';
import Modal from 'umg-ember-backend/components/ui/modal';
import Window from 'umg-ember-backend/components/ui/window';
import { labelValueOptions } from 'umg-ember-backend/utils/intl';

export default class PageBuilderPagesPage extends Component {
  // Services
  @service confirm;
  @service intl;
  @service permission;
  @service recordRelationships;
  @service recordStatus;

  // Defaults
  linkTargets = ['_self', '_blank'];
  linkTypes = ['internal', 'external'];

  // Getter and setter
  get linkTypesOptions() {
    return labelValueOptions.call(this, this.linkTypes, [
      'forms.mlp-page.options.linkType',
    ]);
  }

  get linkTargetsOptions() {
    return labelValueOptions.call(this, this.linkTargets, [
      'forms.mlp-page.options.linkTarget',
    ]);
  }

  // Actions
  confirmRollbackChanges = async () => {
    const scope = 'confirm.discard';

    if (!this.recordRelationships.hasDirtyFields(this.args.page)) {
      return this.args.onClosePage();
    }

    let ok = await this.confirm.ask(`${scope}.title`, `${scope}.message`);

    if (!ok) {
      return false;
    }

    this.args.page.rollbackAttributes();
    this.recordRelationships.rollback(this.args.page);
    return this.args.onClosePage();
  };

  getIsMlpDisabled = (page) => {
    return this.recordStatus.isDisabled(page?.mlp);
  };

  selectLinkType = (option) => {
    this.args.page.linkType = option.value;

    if (this.args.page.isExternal) {
      this.args.page.link = null;

      this.args.page.resetOptions();
    } else {
      this.args.page.linkTarget = '_self';

      this.args.page.setSlugAsLink();
    }
  };

  updateLink = () => {
    if (this.args.page.isInternal) {
      this.args.page.setSlugAsLink();
    }
  };

  toggleStart = () => {
    let currentStartPage = this.args.page.mlp.pages.find((page) => page.start);
    currentStartPage.start = false;
    currentStartPage.setSlugAsLink();

    this.args.page.start = !this.args.page.start;

    this.args.page.setSlugAsLink();
  };

  // Template
  <template>
    <Modal
      @onClose={{this.confirmRollbackChanges}}
      @dataDependencies={{@page}}
      as |page onClose|
    >
      <Window
        @onBodyScroll={{@onBodyScroll}}
        @scrollPosition={{@scrollPosition}}
      >
        <:title>
          {{if
            page.isNew
            (t 'page_builder.pages.page.new_page')
            (t
              'page_builder.pages.page.edit_page' displayLabel=page.displayLabel
            )
          }}
        </:title>

        <:body>
          <Form @model={{page}} as |form|>
            <ContentWithSidebar>
              <:content>
                <form.fieldset @titleKey='general' as |fieldset|>
                  <fieldset.field
                    @property='name'
                    {{on 'keyup' this.updateLink}}
                  />
                </form.fieldset>

                {{#if form.model.mlp.multiPage}}
                  <form.sections.seo />
                {{/if}}
              </:content>
              <:sidebar>
                <form.fieldset @titleKey='settings' as |fieldset|>

                  {{#if form.model.isArtist}}
                    <fieldset.field @property='artist' @type='belongs-to' />
                  {{else}}
                    <fieldset.field
                      @property='linkType'
                      @type='select-value'
                      @options={{this.linkTypesOptions}}
                      @onChange={{this.selectLinkType}}
                    />

                    {{#if form.model.isExternal}}
                      <fieldset.field
                        @property='linkTarget'
                        @type='select-value'
                        @options={{this.linkTargetsOptions}}
                      />
                    {{/if}}

                    <fieldset.field
                      @property='link'
                      @disabled={{form.model.isInternal}}
                      @showValidations={{true}}
                    />
                    <fieldset.field @property='visible' @type='checkbox' />
                    {{#if this.permission.user.isAdmin}}
                      <fieldset.field
                        @checkboxAction={{this.toggleStart}}
                        @property='start'
                        @type='checkbox'
                      />
                    {{/if}}
                  {{/if}}
                </form.fieldset>
              </:sidebar>
            </ContentWithSidebar>

          </Form>
        </:body>

        <:bottombar>
          {{#if (can 'update' page)}}
            <Button
              @disabled={{this.getIsMlpDisabled page}}
              @icon='save'
              @label={{t 'page_builder.pages.page.save'}}
              {{on 'click' @onSavePage}}
            />
          {{/if}}

          <Button
            @icon='close'
            @label={{t 'page_builder.pages.page.close'}}
            @style='grey'
            {{on 'click' onClose}}
          />
        </:bottombar>
      </Window>
    </Modal>
  </template>
}
